import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { getAiProviderStatus, getMarketProviderUsage, resolveUploadUrl } from '../api';
import SDZoneSettingsPanel from '../components/SDZoneSettingsPanel';
import {
  Users,
  ShieldCheck,
  Search,
  RefreshCw,
  Loader2,
  AlertCircle,
  LayoutDashboard,
  Settings,
  LogOut,
  Menu,
  X,
  Trophy,
  Activity,
  Download,
  FileText,
  Filter,
  Flag,
  ShieldAlert,
  TrendingUp,
  Brain,
  Target,
  UserX,
  Crown,
  XCircle,
  ChevronDown,
  DownloadCloud,
  MoreVertical,
  Server,
  CheckCircle,
  Cpu,
  Zap,
  AlertTriangle,
  ChevronUp,
  Trash2,
  HardDrive,
  Wifi,
  Thermometer,
  Sun,
  Moon,
  Clock
} from 'lucide-react';

// ============================================================
// ✅ SECURITY HELPER
// ============================================================

/**
 * Sanitize URL to prevent XSS via javascript: or data: URIs.
 * Only allows standard http and https protocols.
 */
const sanitizeUrl = (url) => {
  if (!url) return null;
  // Allow only http:// or https://
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  // Block everything else (javascript:, data:, vbscript:, relative paths)
  return null;
};

export default function AdminPage() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // State
const [darkMode, setDarkMode] = useState(() => {
  const savedMode = localStorage.getItem('adminTheme');
  return savedMode ? JSON.parse(savedMode) : true;
});
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]); 
  const [loadingReports, setLoadingReports] = useState(false); 
  
  const [systemSettings, setSystemSettings] = useState({
    registration_enabled: true,
    maintenance_mode: false,
  });
  
  const [systemHealth, setSystemHealth] = useState(null);
  const [providerUsage, setProviderUsage] = useState(null);
  const [aiProviders, setAiProviders] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [showAllRegistrations, setShowAllRegistrations] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [modalTab, setModalTab] = useState('overview'); 

  const [actionModal, setActionModal] = useState({ isOpen: false, user: null, type: null });

  const [scoringWeights, setScoringWeights] = useState({
    discipline: 40,
    risk_management: 30,
    consistency: 20,
    growth: 10,
  });

  const [stats, setStats] = useState({
    totalUsers: 0,
    premiumUsers: 0,
    avgEdgeScore: 0,
    revenue: 0,
  });

  // Effects
  useEffect(() => {
    if (currentUser && !currentUser.is_admin) {
      navigate('/dashboard'); 
    }
  }, [currentUser, navigate]);

 useEffect(() => {
  // 1. Apply class to DOM
  if (darkMode) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
  
  // 2. Save preference to localStorage
  localStorage.setItem('adminTheme', JSON.stringify(darkMode));
}, [darkMode]);

  const fetchReports = async () => {
    setLoadingReports(true);
    try {
      const res = await api.get('/admin/reports');
      setReports(res.data.items || res.data || []);
    } catch (err) {
      console.error("Failed to fetch reports", err);
    } finally {
      setLoadingReports(false);
    }
  };

  const fetchProviderUsage = async () => {
    setUsageLoading(true);
    try {
      const res = await getMarketProviderUsage();
      setProviderUsage(res.data || null);
    } catch (err) {
      console.error("Failed to fetch provider usage", err);
      setProviderUsage(null);
    } finally {
      setUsageLoading(false);
    }
  };

  const fetchAiProviders = async () => {
    try {
      const res = await getAiProviderStatus();
      setAiProviders(res.data?.providers || []);
    } catch (err) {
      console.error("Failed to fetch AI provider status", err);
      setAiProviders([]);
    }
  };

  useEffect(() => {
    if (activeTab === 'reports') {
      fetchReports();
    }
    if (activeTab === 'markets') {
      fetchProviderUsage();
    }
    if (activeTab === 'system') {
      fetchAiProviders();
    }
  }, [activeTab]);

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) setIsRefreshing(true);
    else setLoading(true);
    
    setError('');
    try {
      const userRes = await api.get('/admin/users');
      const userList = userRes.data.users || [];
      
      setUsers(userList);
      
      const premium = userList.filter(u => u.plan_type === 'premium').length;
      const scores = userList.map(u => u.edge_score || 0).filter(s => s > 0);
      const avgScore = scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
      
      setStats({
        totalUsers: userList.length,
        premiumUsers: premium,
        avgEdgeScore: Math.floor(avgScore),
        revenue: premium * 3.5
      });

      try {
        const settingsRes = await api.get('/admin/settings');
        setSystemSettings(settingsRes.data || systemSettings);
      } catch (e) { console.warn("Settings endpoint not found"); }

      try {
        // Fetch health data which includes worker status
        const healthRes = await api.get('/system/health');
        setSystemHealth(healthRes.data);
      } catch (e) {
        console.warn("Health check failed", e);
        setSystemHealth({ status: 'unhealthy', error: e.message });
      }

    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || 'Failed to fetch data.');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (currentUser?.is_admin) {
      fetchData();
    }
  }, [currentUser]);

  const handleDeleteReportedPost = async (postId, reportId) => {
    if (!window.confirm("Delete this post permanently? This will also dismiss the report.")) return;
    try {
      await api.delete(`/admin/reports/${reportId}/delete-post`);
      setReports(prev => prev.filter(r => r.id !== reportId));
      alert("Post deleted successfully.");
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || "Failed to delete post.");
    }
  };

const handleDismissReport = async (reportId) => {
  if (!window.confirm("Dismiss this report? The post will remain.")) return;
  
  try {
    // ✅ FIXED: Correct path
    await api.post(`/admin/reports/${reportId}/dismiss`);
    
    setReports(prev => prev.filter(r => r.id !== reportId));
    alert("Report dismissed.");
  } catch (err) {
    console.error(err);
    alert("Failed to dismiss report.");
  }
};

  const handleSettingChange = async (key, value) => {
    try {
      await api.put('/admin/settings', { [key]: value });
      setSystemSettings(prev => ({ ...prev, [key]: value }));
    } catch (err) { 
      console.error(err);
      alert('Failed to update setting'); 
    }
  };

  const handleGrantPremium = async (userId) => {
    if (!window.confirm("Grant 30 days of Premium access to this user?")) return;
    setIsSaving(true);
    try {
      await api.post(`/admin/user/${userId}/grant-premium`, { days: 30 });
      alert("Premium granted successfully.");
      fetchData();
    } catch (err) {
      console.error(err);
      alert("Failed to grant premium.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevokePremium = async (userId) => {
    if (!window.confirm("Are you sure you want to revoke Premium access?")) return;
    setIsSaving(true);
    try {
      await api.post(`/admin/user/${userId}/revoke-premium`);
      alert("Premium revoked.");
      fetchData();
    } catch (err) {
      console.error(err);
      alert("Failed to revoke premium.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReleaseTradingLock = async (userId) => {
    if (!window.confirm("Release this user's psychology trading lock and reset strikes?")) return;
    setIsSaving(true);
    try {
      await api.post(`/admin/user/${userId}/release-trading-lock`);
      alert("Trading lock released and strikes reset.");
      fetchData(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to release trading lock.");
    } finally { setIsSaving(false); }
  };

  const openActionModal = (user, type) => {
    setActionModal({ isOpen: true, user, type });
  };

   const handleConfirmAction = async () => {
    const { user, type } = actionModal;
    if (!user) return;

    setIsSaving(true);
    try {
      if (type === 'suspend') {
        await api.post(`/admin/user/${user.id}/suspend`);
        alert("User suspended successfully.");
      } else if (type === 'recover') {
        await api.post(`/admin/user/${user.id}/recover`);
        alert("User recovered successfully.");
      } else if (type === 'delete') {
        await api.delete(`/admin/user/${user.id}/delete`);
        alert("User permanently deleted.");
      }
      
      await fetchData(); 
      
      setActionModal({ isOpen: false, user: null, type: null });
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || "Action failed.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRecalculateScores = async () => {
    setIsSaving(true);
    try {
      await new Promise(r => setTimeout(r, 800));
      alert("Scoring engine updated and scores recalculated for all users.");
    } catch (err) {
      alert("Failed to recalculate scores.");
    } finally {
      setIsSaving(false);
    }
  };

  const downloadReport = async () => {
    try {
      const response = await api.get('/admin/reports/users-csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'user_report.csv');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Error downloading report", error);
      alert("Failed to download report.");
    }
  };

  const filteredUsers = users.filter(u => 
    (u.email && u.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (u.name && u.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleNavClick = (tab) => {
    setActiveTab(tab);
    setIsSidebarOpen(false);
  };

  const openUserModal = (user) => {
    setSelectedUser(user);
    setModalTab('overview');
    setIsModalOpen(true);
  };

  const getStatusBadge = (status) => {
    if (status === 'healthy') return <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-500/20"><CheckCircle size={12} /> Operational</span>;
    if (status === 'degraded') return <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold bg-yellow-100 dark:bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-500/20"><AlertCircle size={12} /> Degraded</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold bg-red-100 dark:bg-red-500/10 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/20"><XCircle size={12} /> Offline/Error</span>;
  };

  const timeAgo = (dateString) => {
    if (!dateString) return "Unknown";
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " years ago";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " months ago";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " days ago";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " hours ago";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " minutes ago";
    return "Just now";
  };

  if (!currentUser?.is_admin) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center p-6 bg-white dark:bg-gray-900 rounded-xl border border-red-200 dark:border-red-500/30 shadow-sm">
          <AlertCircle size={40} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl text-gray-900 dark:text-white font-bold">Access Denied</h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">You do not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 flex flex-col md:flex-row transition-colors duration-300 overflow-x-hidden">
      
      {/* SIDEBAR */}
      {isSidebarOpen && <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setIsSidebarOpen(false)} />}
      <aside className={`fixed top-0 left-0 h-full w-72 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col z-50 transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:static no-scrollbar overflow-y-auto`}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2"><ShieldCheck className="text-blue-500" /> Admin Panel</h1>
            <p className="text-[10px] text-gray-500 mt-1">System Control</p>
          </div>
          <button onClick={() => setIsSidebarOpen(false)} className="text-gray-400 md:hidden"><X size={24} /></button>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <button onClick={() => handleNavClick("overview")} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'overview' ? 'bg-blue-50 dark:bg-blue-600/10 text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
            <LayoutDashboard size={18} /><span className="font-medium">Overview</span>
          </button>
          <button onClick={() => handleNavClick("reports")} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'reports' ? 'bg-red-50 dark:bg-red-600/10 text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
            <Flag size={18} /><span className="font-medium">Reported Content</span>
            {reports.length > 0 && (<span className="ml-auto bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 text-[10px] font-bold px-2 py-0.5 rounded-full">{reports.length}</span>)}
          </button>
          <button onClick={() => handleNavClick("performance")} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'performance' ? 'bg-blue-50 dark:bg-blue-600/10 text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
            <Trophy size={18} /><span className="font-medium">Leaderboard</span>
          </button>
          <button onClick={() => handleNavClick("users")} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'users' ? 'bg-blue-50 dark:bg-blue-600/10 text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
            <Users size={18} /><span className="font-medium">User Management</span>
          </button>
          <button onClick={() => handleNavClick("system")} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'system' ? 'bg-blue-50 dark:bg-blue-600/10 text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
            <Settings size={18} /><span className="font-medium">System Controls</span>
          </button>
          <button onClick={() => handleNavClick("markets")} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'markets' ? 'bg-blue-50 dark:bg-blue-600/10 text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
            <TrendingUp size={18} /><span className="font-medium">Market Data</span>
          </button>
          <button onClick={() => handleNavClick("zones")} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'zones' ? 'bg-blue-50 dark:bg-blue-600/10 text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
            <Target size={18} /><span className="font-medium">Zone Settings</span>
          </button>
        </nav>
       <div className="p-4 border-t border-gray-200 dark:border-gray-800 space-y-2">
          <button onClick={downloadReport} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium">
            <DownloadCloud size={16} /> Download Report
          </button>
          
          <button 
            onClick={() => setDarkMode(!darkMode)} 
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors text-sm font-medium"
          >
            {darkMode ? <Sun size={16} /> : <Moon size={16} />}
            {darkMode ? 'Light Mode' : 'Dark Mode'}
          </button>

          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors text-sm">
            <LogOut size={16} /> Logout
          </button>
      </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 flex flex-col min-h-screen">
        <div className="sticky top-0 z-30 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 p-4 flex justify-between items-center md:hidden">
          <h1 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
             {activeTab === 'performance' && <Trophy className="text-yellow-500" />}
             {activeTab === 'users' && <Users className="text-blue-500" />}
             {activeTab === 'reports' && <Flag className="text-red-500" />}
             {activeTab === 'overview' && 'Overview'}
             {activeTab === 'system' && 'Settings'}
             {activeTab === 'markets' && 'Market Data'}
             {activeTab === 'zones' && 'Zone Settings'}
          </h1>
          <button onClick={() => setIsSidebarOpen(true)} className="text-gray-600 dark:text-gray-300">
            <Menu size={24} />
          </button>
        </div>

        <div className="flex-1 p-4 md:p-8 pb-32 overflow-y-auto no-scrollbar">
          {loading ? (
            <div className="flex justify-center py-24"><Loader2 className="animate-spin text-blue-500" size={40} /></div>
          ) : error ? (
            <div className="text-red-500 text-center py-12">{error}</div>
          ) : (
            <>
              {/* OVERVIEW */}
              {activeTab === "overview" && (
                <div>
                  <div className="mb-8">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Welcome back, {currentUser?.name || 'Admin'} 👋</h2>
                    <p className="text-gray-500 dark:text-gray-400 text-sm md:text-base mt-1">Here is what's happening with your platform today.</p>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <StatCard icon={Users} label="Total Users" value={stats.totalUsers} color="blue" />
                    <StatCard icon={Crown} label="Premium Users" value={stats.premiumUsers} color="yellow" />
                    <StatCard icon={Activity} label="Avg EDGE Score" value={stats.avgEdgeScore} color="green" />
                    <StatCard icon={TrendingUp} label="Revenue" value={`$${stats.revenue}`} color="purple" />
                  </div>
                  
                  {/* FIXED: Infrastructure Health Card */}
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 mb-8 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"><Server size={20} className="text-blue-500" /> Infrastructure Health</h3>
                          {systemHealth && getStatusBadge(systemHealth.status)}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700/50">
                              <p className="text-xs text-gray-500 mb-1">API Status</p>
                              <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                                  <CheckCircle size={14} className="text-green-400" />
                                  Online
                              </p>
                          </div>
                          
                          {/* Dynamic Worker Check */}
                          <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700/50">
                              <p className="text-xs text-gray-500 mb-1">MT5 Sync Worker</p>
                              <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                                  <Cpu size={14} className={systemHealth?.workers?.some(w => w.status === 'Running') ? "text-green-400" : "text-red-400"} />
                                  {systemHealth?.workers?.find(w => w.name === 'MT5 Sync Worker')?.status || 'Offline'}
                              </p>
                          </div>

                          {/* Dynamic Last Sync */}
                          <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700/50">
                              <p className="text-xs text-gray-500 mb-1">Last Data Sync</p>
                              <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                                  <Zap size={14} className="text-yellow-500" />
                                  {systemHealth?.workers?.[0]?.last_sync ? timeAgo(systemHealth.workers[0].last_sync) : 'Never'}
                              </p>
                          </div>
                      </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent User Registrations</h3>
                    <div className="space-y-4">
                       {(() => {
                          const sortedUsers = users.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                          const usersToShow = showAllRegistrations ? sortedUsers : sortedUsers.slice(0, 3);
                          return (
                            <>
                              {usersToShow.map((user, i) => (
                                <div key={user.id || i} className="flex items-center gap-4 text-sm border-b border-gray-100 dark:border-gray-800 pb-2 last:border-0 last:pb-0">
                                    <div className="p-2 bg-green-100 dark:bg-green-500/10 rounded-lg"><Users size={16} className="text-green-500"/></div>
                                    <div className="flex-1"><p className="text-gray-700 dark:text-gray-300"><span className="font-medium text-gray-900 dark:text-white">{user.name || 'New User'}</span> joined the platform.</p><p className="text-gray-400 dark:text-gray-500 text-xs">{user.email}</p></div>
                                    <div className="text-gray-400 dark:text-gray-500 text-xs">{timeAgo(user.created_at)}</div>
                                </div>
                              ))}
                              {sortedUsers.length > 3 && (<button onClick={() => setShowAllRegistrations(!showAllRegistrations)} className="mt-2 w-full text-center text-xs text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 flex items-center justify-center gap-1 py-2">{showAllRegistrations ? (<>Show Less <ChevronUp size={14} /></>) : (<>Show {sortedUsers.length - 3} More <ChevronDown size={14} /></>)}</button>)}
                            </>
                          );
                       })()}
                       {users.length === 0 && (<p className="text-gray-400 text-center py-4 text-sm">No user registrations yet.</p>)}
                    </div>
                  </div>
                </div>
              )}

              {/* REPORTS */}
{activeTab === "reports" && (
  <div className="space-y-6">
    {/* Header */}
    <div className="flex justify-between items-center">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Flag className="text-red-500" /> Reported Content
        </h2>
        <p className="text-gray-500 text-sm">Review and manage user reports.</p>
      </div>
      <button 
        onClick={fetchReports} 
        className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
      >
        <RefreshCw size={18} className={loadingReports ? 'animate-spin' : ''} />
      </button>
    </div>

    {/* Content State */}
    {loadingReports ? (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin text-gray-500" size={32} />
      </div>
    ) : reports.length === 0 ? (
      <div className="text-center py-12 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl">
        <CheckCircle size={40} className="mx-auto text-green-500 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">All Clear!</h3>
        <p className="text-gray-400 text-sm mt-1">There are no pending reports.</p>
      </div>
    ) : (
      <div className="space-y-4">
        {reports.map((report) => {
          // ✅ SECURITY FIX: Sanitize the image URL to prevent XSS
          // only allow http/https protocols. 
          const safeImageUrl = report.post_image && /^https?:\/\//i.test(report.post_image) 
            ? report.post_image 
            : null;

          return (
            <div key={report.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5 shadow-sm">
              {/* Report Meta */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 text-[10px] font-bold bg-red-100 dark:bg-red-500/10 text-red-600 dark:text-red-400 rounded uppercase">
                    Pending
                  </span>
                  <span className="text-xs text-gray-400">{timeAgo(report.created_at)}</span>
                </div>
                <span className="text-xs text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                  Reason: <span className="font-medium text-gray-700 dark:text-gray-300">{report.reason || 'No reason provided'}</span>
                </span>
              </div>

              {/* Reported Content Preview */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-700/50 mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-600 dark:text-white">
                    {report.post_author_name?.[0] || 'U'}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{report.post_author_name || 'Unknown User'}</p>
                    <p className="text-xs text-gray-400">Reported Post Author</p>
                  </div>
                </div>
                
                <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 italic mb-3">
                  "{report.post_content || 'Post content unavailable'}"
                </p>

                {/* ✅ SECURITY FIX: Only render image if safeImageUrl is not null */}
                {safeImageUrl && (
                  <div className="mt-2 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 max-w-sm">
                    <img 
                      src={safeImageUrl} 
                      alt="Reported content" 
                      className="w-full h-auto object-cover max-h-60" 
                    />
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-2 border-t border-gray-100 dark:border-gray-800">
                <div className="text-xs text-gray-400">
                  Reported by: <span className="font-medium text-gray-600 dark:text-gray-300">{report.reporter_name || 'Anonymous'}</span>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleDismissReport(report.id)} 
                    className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    Dismiss
                  </button>
                  <button 
                    onClick={() => handleDeleteReportedPost(report.post_id, report.id)} 
                    className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <Trash2 size={12} /> Delete Post
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
)}
            {/* PERFORMANCE */}
{activeTab === "performance" && (
  <div className="space-y-6">
    {/* Header */}
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
        <Trophy className="text-yellow-500" /> Performance Leaderboard
      </h2>
      <p className="text-gray-500 dark:text-gray-400 text-sm">Ranked by EDGE Score algorithm.</p>
    </div>

    {/* Search Only (Removed All Time Filter) */}
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 rounded-xl flex flex-wrap gap-4 items-center shadow-sm">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
        <input 
          type="text" 
          placeholder="Search user..." 
          value={searchTerm} 
          onChange={(e) => setSearchTerm(e.target.value)} 
          className="w-full bg-slate-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg py-2 pl-9 pr-4 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-blue-500" 
        />
      </div>
    </div>

    {/* Leaderboard Table */}
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto no-scrollbar">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider">
              <th className="p-4">Rank</th>
              <th className="p-4">User</th>
              <th className="p-4 text-right">EDGE Score</th>
              <th className="p-4 text-right">Growth %</th>
              <th className="p-4 text-right">Net Profit</th>
              <th className="p-4 text-right">Win Rate</th>
              <th className="p-4 text-right">DD %</th>
              <th className="p-4 text-center">Plan</th>
              <th className="p-4 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-sm">
            {(() => {
              // FIX: Sort users by EDGE Score (Best stats at top)
              const sortedUsers = [...filteredUsers].sort((a, b) => (b.edge_score || 0) - (a.edge_score || 0));

              if (sortedUsers.length === 0) {
                return (
                  <tr>
                    <td colSpan="9" className="p-8 text-center text-gray-400">No users found.</td>
                  </tr>
                );
              }

              return sortedUsers.map((user, index) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors group">
                  <td className="p-4 font-bold text-gray-400">
                    #{index + 1}
                    {index === 0 && <Crown size={12} className="inline ml-1 text-yellow-500" />}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {user.avatar ? (
                        <img src={resolveUploadUrl(user.avatar)} alt={user.name || 'User'} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 flex items-center justify-center text-xs font-bold text-white">
                          {user.email ? user.email[0].toUpperCase() : 'U'}
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{user.name || 'User'}</p>
                        <p className="text-xs text-gray-400">{user.email || 'No Email'}</p>
                      </div>
                    </div>
                  </td>
                  <td className={`p-4 text-right font-bold ${(user.edge_score || 0) > 80 ? 'text-green-500' : 'text-yellow-500'}`}>
                    {user.edge_score || 0}
                  </td>
                  <td className={`p-4 text-right ${(user.growth || 0) > 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {user.growth > 0 ? '+' : ''}{user.growth || 0}%
                  </td>
                  <td className="p-4 text-right text-gray-700 dark:text-gray-300">${user.net_profit || 0}</td>
                  <td className="p-4 text-right text-gray-700 dark:text-gray-300">{user.win_rate || 0}%</td>
                  <td className="p-4 text-right text-red-500">{user.max_drawdown || 0}%</td>
                  <td className="p-4 text-center">
                    {user.plan_type === 'premium' ? (
                      <span className="px-2 py-1 text-[10px] bg-yellow-100 dark:bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 rounded-full border border-yellow-200 dark:border-yellow-500/20">PRO</span>
                    ) : (
                      <span className="text-gray-400 text-xs">FREE</span>
                    )}
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button 
                        onClick={() => openUserModal(user)} 
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-white p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" 
                        title="View Performance"
                      >
                        <Activity size={16} />
                      </button>
                      <button 
                        onClick={() => openUserModal(user)} 
                        className="text-gray-400 hover:text-blue-500 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" 
                        title="Download Report"
                      >
                        <DownloadCloud size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ));
            })()}
          </tbody>
        </table>
      </div>
    </div>
  </div>
)}

              {/* USER MANAGEMENT */}
              {activeTab === "users" && (
                <div>
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                    <div><h2 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h2><p className="text-gray-500 text-sm">Manage subscriptions, permissions, and account status.</p></div>
                    <div className="relative w-full md:w-64"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} /><input type="text" placeholder="Search by email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg py-2 pl-9 pr-4 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-blue-500" /></div>
                  </div>
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden shadow-sm">
                    {filteredUsers.length === 0 ? (<div className="p-12 text-center text-gray-400">No users found.</div>) : (
                      <div className="divide-y divide-gray-100 dark:divide-gray-800">
                        {filteredUsers.map(user => (
                          <div key={user.id} className="p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                            <div className="flex items-center gap-4 w-full md:w-auto">
                              {user.avatar ? (
                                <img src={resolveUploadUrl(user.avatar)} alt={user.name || 'User'} className="w-12 h-12 rounded-full object-cover shrink-0 border border-gray-200 dark:border-gray-700" />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg shrink-0">{user.email ? user.email[0].toUpperCase() : 'U'}</div>
                              )}
                              <div>
                                <p className="text-gray-900 dark:text-white font-medium">{user.name || 'No Name'}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{user.email || 'No Email'}</p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    {user.plan_type === 'premium' ? (<span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold bg-yellow-100 dark:bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 rounded-full border border-yellow-200 dark:border-yellow-500/20"><Crown size={10} /> PREMIUM</span>) : (<span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full">FREE</span>)}
                                    {user.is_deleted === true || user.is_active === false ? (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold bg-red-100 dark:bg-red-500/10 text-red-600 dark:text-red-400 rounded-full border border-red-200 dark:border-red-500/20"><XCircle size={10} /> SUSPENDED</span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 rounded-full border border-green-200 dark:border-green-500/20"><CheckCircle size={10} /> ACTIVE</span>
                                    )}
                                    <span className="text-xs text-gray-400">ID: {user.id}</span>
                                    {user.markets_enabled && <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold bg-sky-100 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300 rounded-full">MARKETS BETA</span>}
                                    {user.trading_lock?.locked && <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold bg-red-100 dark:bg-red-500/10 text-red-700 dark:text-red-300 rounded-full">LOCKED {user.trading_lock.strikes}/3</span>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 w-full md:w-auto justify-end">
                              {user.trading_lock?.locked && <button onClick={() => handleReleaseTradingLock(user.id)} disabled={isSaving} className="px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-xs font-medium rounded-lg disabled:opacity-50">Release lock</button>}
                              {user.plan_type !== 'premium' ? (<button onClick={() => handleGrantPremium(user.id)} disabled={isSaving} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"><Crown size={14} /> Upgrade</button>) : (<button onClick={() => handleRevokePremium(user.id)} disabled={isSaving} className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"><XCircle size={14} /> Downgrade</button>)}
                              
                              {user.is_deleted || user.is_active === false ? (
                                <button onClick={() => openActionModal(user, 'recover')} disabled={isSaving} className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-500/30 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400 text-sm font-medium rounded-lg transition-colors disabled:opacity-50" title="Unsuspend User"><CheckCircle size={14} /> Unsuspend</button>
                              ) : (
                                <button onClick={() => openActionModal(user, 'suspend')} disabled={isSaving} className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" title="Suspend User"><UserX size={18} /></button>
                              )}

                              <button onClick={() => openActionModal(user, 'delete')} disabled={isSaving} className="p-2 text-gray-400 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" title="Delete Permanently"><Trash2 size={18} /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

             {/* SYSTEM TAB */}
{activeTab === "system" && (
  <div className="space-y-8">
    {/* --- HEADER --- */}
    <div className="mb-6">
      <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
        <Server size={22} className="text-blue-500" /> System Health Monitor
      </h3>
    </div>

    {/* --- ROW 1: STATUS & RESOURCES (CPU / RAM) --- */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* API Status */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm">
        <div className="flex justify-between items-start mb-4">
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">API Status</h4>
          {systemHealth ? getStatusBadge(systemHealth.status) : <Loader2 size={16} className="animate-spin text-gray-500" />}
        </div>
        <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1 capitalize">{systemHealth?.status || 'Checking...'}</div>
        <p className="text-xs text-gray-400">Uptime: 99.9%</p>
      </div>

      {/* CPU Usage */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">CPU Load</h4>
          <span className="text-xl font-bold text-blue-500">{systemHealth?.cpu?.percent || 0}%</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-4">
          <div className="bg-blue-500 h-2.5 rounded-full" style={{ width: `${systemHealth?.cpu?.percent || 0}%` }}></div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
          <div><span className="font-medium text-gray-700 dark:text-gray-300">{systemHealth?.cpu?.count_logical}</span> Logical Cores</div>
          <div><span className="font-medium text-gray-700 dark:text-gray-300">{systemHealth?.cpu?.freq_current?.toFixed(0) || 0} Mhz</span> Freq</div>
        </div>
      </div>

      {/* Memory Usage */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Memory (RAM)</h4>
          <span className="text-xl font-bold text-purple-500">{systemHealth?.memory?.percent || 0}%</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-4">
          <div className="bg-purple-500 h-2.5 rounded-full" style={{ width: `${systemHealth?.memory?.percent || 0}%` }}></div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
          <div><span className="font-medium text-gray-700 dark:text-gray-300">{systemHealth?.memory?.used_gb || 0}</span> GB Used</div>
          <div><span className="font-medium text-gray-700 dark:text-gray-300">{systemHealth?.memory?.total_gb || 0}</span> GB Total</div>
        </div>
      </div>
    </div>

    {/* --- ROW 1b: AI PROVIDER FALLBACK CHAIN --- */}
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm">
      <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
        <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2"><Brain size={16} className="text-gray-400" /> AI Provider Fallback Chain</h4>
        <span className="text-[10px] text-gray-400">Order: OpenAI → Groq → Gemini → OpenRouter → Mistral</span>
      </div>
      {aiProviders === null ? (
        <p className="text-xs text-gray-400">Loading provider status...</p>
      ) : aiProviders.length === 0 ? (
        <p className="text-xs text-gray-400">Provider status unavailable.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {aiProviders.map((p) => (
            <div key={p.name} className="flex items-center justify-between gap-3 bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{p.label}</p>
                <p className="text-[10px] text-gray-400 font-mono truncate">{p.model}</p>
              </div>
              {!p.configured ? (
                <span className="shrink-0 text-[10px] font-bold px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-500">No key</span>
              ) : p.cooling_down ? (
                <span className="shrink-0 text-[10px] font-bold px-2 py-1 rounded bg-red-100 dark:bg-red-500/10 text-red-600" title={p.last_error || 'Cooling down'}>{p.last_error || 'Cooldown'} · {p.cooldown_seconds}s</span>
              ) : (
                <span className="shrink-0 flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded bg-green-100 dark:bg-green-500/10 text-green-600"><Zap size={10} /> Ready</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>

    {/* --- ROW 2: DISKS & NETWORK --- */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Disk Partitions */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm">
        <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2"><HardDrive size={16} className="text-gray-400" /> Disk Usage</h4>
        <div className="space-y-4">
          {systemHealth?.disks?.length > 0 ? systemHealth.disks.map((disk, i) => (
            <div key={i}>
              <div className="flex justify-between text-xs mb-1">
                <span className="font-mono text-gray-600 dark:text-gray-300">{disk.mountpoint}</span>
                <span className="font-bold text-gray-900 dark:text-white">{disk.percent}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div className={`${disk.percent > 90 ? 'bg-red-500' : 'bg-green-500'} h-1.5 rounded-full`} style={{ width: `${disk.percent}%` }}></div>
              </div>
              <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                <span>{disk.fstype}</span>
                <span>{disk.free_gb} GB free</span>
              </div>
            </div>
          )) : <p className="text-xs text-gray-400">No disk data available.</p>}
        </div>
      </div>

      {/* Network I/O */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm">
        <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2"><Wifi size={16} className="text-gray-400" /> Network Traffic</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
            <p className="text-[10px] text-gray-400 uppercase">Sent</p>
            <p className="text-lg font-bold text-blue-500">{systemHealth?.network?.bytes_sent_gb || 0} <span className="text-xs font-normal text-gray-400">GB</span></p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
            <p className="text-[10px] text-gray-400 uppercase">Received</p>
            <p className="text-lg font-bold text-green-500">{systemHealth?.network?.bytes_recv_gb || 0} <span className="text-xs font-normal text-gray-400">GB</span></p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
            <p className="text-[10px] text-gray-400 uppercase">Packets Sent</p>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{systemHealth?.network?.packets_sent?.toLocaleString() || 0}</p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
            <p className="text-[10px] text-gray-400 uppercase">Connections</p>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{systemHealth?.network?.connections_count || 0}</p>
          </div>
        </div>
      </div>
    </div>

    {/* --- ROW 3: PROCESSES ONLY (Sensors Removed) --- */}
    {/* Changed grid to grid-cols-1 to let Processes take full width */}
    <div className="grid grid-cols-1 gap-6">
      {/* Processes */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm">
        <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2"><Activity size={16} className="text-gray-400" /> Processes</h4>
        <div className="flex gap-4 mb-4 text-xs">
          <span className="px-2 py-1 bg-blue-100 dark:bg-blue-500/10 text-blue-600 rounded font-bold">Total: {systemHealth?.processes?.total || 0}</span>
          <span className="px-2 py-1 bg-green-100 dark:bg-green-500/10 text-green-600 rounded font-bold">Running: {systemHealth?.processes?.running || 0}</span>
        </div>
        
        <div className="space-y-3">
          <div>
            <p className="text-[10px] uppercase text-gray-400 font-bold mb-1">Top CPU Consumers</p>
            <div className="space-y-1">
              {systemHealth?.processes?.top_cpu?.map((p, i) => (
                <div key={i} className="flex justify-between text-xs bg-gray-50 dark:bg-gray-800 p-2 rounded">
                  <span className="font-mono truncate">{p.name}</span>
                  <span className="font-bold text-red-500">{p.cpu.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* --- ROW 4: SYNC WORKERS --- */}
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm">
      <div className="flex justify-between items-start mb-4">
        <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Background Workers</h4>
      </div>
      
      <div className="space-y-3">
        {systemHealth?.workers && systemHealth.workers.length > 0 ? (
          systemHealth.workers.map((worker, idx) => (
            <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700/50">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-bold text-gray-900 dark:text-white">{worker.name}</p>
                  <p className="text-xs text-gray-400 font-mono">PID: {worker.pid || 'N/A'}</p>
                </div>
                <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                  worker.status === 'Running' 
                  ? 'bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400' 
                  : 'bg-red-100 dark:bg-red-500/10 text-red-600 dark:text-red-400'
                }`}>
                  {worker.status}
                </span>
              </div>
              
              <div className="flex items-center justify-between text-xs border-t border-gray-100 dark:border-gray-700 pt-3 mt-3">
                <div className="flex items-center gap-1.5 text-gray-500">
                  <Clock size={12} /> Last Heartbeat:
                </div>
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {worker.last_sync ? timeAgo(worker.last_sync) : 'Never'}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="text-xs text-red-500 bg-red-50 dark:bg-red-500/10 px-4 py-3 rounded-lg flex items-center gap-2">
            <XCircle size={14} />
            No active workers detected
          </div>
        )}
      </div>
    </div>

    {/* --- ROW 5: ERROR LOGS --- */}
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 mb-8 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"><AlertTriangle className="text-red-500" /> System Error Logs</h3>
        <span className="text-xs text-gray-400">Last 24h</span>
      </div>
      <div className="space-y-2 max-h-64 overflow-y-auto no-scrollbar pr-2">
        {systemHealth?.errors?.length > 0 ? (
          systemHealth.errors.map((err, i) => (
            <div key={i} className="p-3 bg-red-50 dark:bg-red-500/5 border border-red-200 dark:border-red-500/10 rounded-lg text-xs text-red-400 dark:text-red-300 font-mono">
              <div className="flex justify-between items-center mb-1">
                <span className="font-bold text-red-500">{err.level || 'ERROR'}</span>
                <span className="text-gray-400">{err.timestamp || 'Just now'}</span>
              </div>
              <p>{err.message}</p>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400">
            <CheckCircle size={32} className="text-green-500 mb-2" />
            <p className="font-medium text-gray-600 dark:text-gray-500">No critical errors detected</p>
            <p className="text-xs text-gray-400">All systems operational</p>
          </div>
        )}
      </div>
    </div>

    {/* --- ROW 6: CONFIGURATION --- */}
    <div className="border-t border-gray-200 dark:border-gray-800 pt-8">
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2"><Settings size={22} className="text-gray-400" /> Configuration</h3>
      <div className="max-w-xl space-y-4">
        <SettingToggle label="User Registration" description="Enable new signups" value={systemSettings.registration_enabled} onChange={(v) => handleSettingChange('registration_enabled', v)} />
        <SettingToggle label="Maintenance Mode" description="Take site offline" value={systemSettings.maintenance_mode} onChange={(v) => handleSettingChange('maintenance_mode', v)} isDanger />
      </div>
    </div>
  </div>
)}

{activeTab === "markets" && (
  <div className="space-y-8">
    {/* --- HEADER --- */}
    <div className="mb-6">
      <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
        <TrendingUp size={22} className="text-blue-500" /> Market Data Providers
      </h3>
    </div>

    {usageLoading && !providerUsage ? (
      <div className="flex justify-center py-24"><Loader2 className="animate-spin text-blue-500" size={40} /></div>
    ) : !providerUsage ? (
      <div className="text-xs text-red-500 bg-red-50 dark:bg-red-500/10 px-4 py-3 rounded-lg flex items-center gap-2">
        <AlertTriangle size={14} /> Provider usage could not be loaded. Market access and admin rights are required.
      </div>
    ) : (
      <>
        {/* --- ROW 1: BUDGETS & CONFIG --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Daily Request Budget</h4>
            <div className="space-y-4">
              {Object.entries(providerUsage.limits || {}).map(([provider, limit]) => {
                const used = (providerUsage.usage || []).find(u => u.provider === provider)?.request_count || 0;
                const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
                return (
                  <div key={provider}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700 dark:text-gray-300 capitalize">{provider}</span>
                      <span className="font-bold text-gray-900 dark:text-white tabular-nums">{used} / {limit}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div className={`${pct > 85 ? 'bg-red-500' : pct > 60 ? 'bg-yellow-500' : 'bg-green-500'} h-1.5 rounded-full`} style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-gray-400 mt-4">Budgets reset daily (UTC). Read-through caches and stale fallbacks absorb most traffic, so live requests should stay well under the caps.</p>
          </div>

          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Provider Configuration</h4>
            <div className="space-y-2">
              {Object.entries(providerUsage.providers_configured || {}).map(([provider, configured]) => (
                <div key={provider} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-800 p-2.5 rounded">
                  <span className="font-medium text-gray-700 dark:text-gray-300 capitalize">{provider}</span>
                  <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${configured ? 'bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                    {configured ? 'Key configured' : 'No key'}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-4">Frankfurter and CoinGecko are keyless. A provider without a key serves nothing until its key is added to the environment.</p>
          </div>
        </div>

        {/* --- ROW 2: TWELVE DATA BREAKER --- */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm">
          <div className="flex justify-between items-start mb-4">
            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2"><Activity size={16} className="text-gray-400" /> Twelve Data Circuit Breaker</h4>
            <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${providerUsage.twelvedata_breaker?.opened ? 'bg-red-100 dark:bg-red-500/10 text-red-600 dark:text-red-400' : 'bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400'}`}>
              {providerUsage.twelvedata_breaker?.opened ? 'Open (recovering)' : 'Closed'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
              <p className="text-[10px] text-gray-400 uppercase">Consecutive failures</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{providerUsage.twelvedata_breaker?.failures ?? 0}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
              <p className="text-[10px] text-gray-400 uppercase">Auto-recovery window</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">120s</p>
            </div>
          </div>
          <p className="text-[10px] text-gray-400 mt-4">
            The breaker opens after 4 consecutive auth or rate-limit failures and retries automatically every 2 minutes, so a replaced API key takes effect without a restart.
            While it is open, FX quotes fall back to Frankfurter reference rates and crypto to CoinGecko.
          </p>
        </div>

        {/* --- ROW 3: USAGE LEDGER --- */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 mb-8 shadow-sm">
          <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">Usage Ledger (today, UTC)</h4>
          {(providerUsage.usage || []).length ? (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-800">
                  <th className="pb-2 font-semibold">Provider</th>
                  <th className="pb-2 font-semibold">Date</th>
                  <th className="pb-2 font-semibold text-right">Requests</th>
                </tr>
              </thead>
              <tbody>
                {providerUsage.usage.map((row) => (
                  <tr key={row.provider} className="border-b border-gray-50 dark:border-gray-800/50">
                    <td className="py-2 font-medium text-gray-700 dark:text-gray-300 capitalize">{row.provider}</td>
                    <td className="py-2 text-gray-500 font-mono">{row.usage_date}</td>
                    <td className="py-2 text-right font-bold text-gray-900 dark:text-white tabular-nums">{row.request_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-xs text-gray-400">No provider requests recorded today.</p>
          )}
        </div>
      </>
    )}
  </div>
)}

{activeTab === "zones" && <SDZoneSettingsPanel />}

            </>
          )}
        </div>
      </main>

     {/* USER PROFILE MODAL */}
{isModalOpen && selectedUser && (
  <div className="fixed inset-0 z-50 overflow-y-auto">
    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 transition-opacity bg-slate-100 dark:bg-gray-950 opacity-90" 
        onClick={() => setIsModalOpen(false)} 
      />
      
      {/* Modal Container */}
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-5xl w-full border border-gray-200 dark:border-gray-800 flex flex-col max-h-[90vh]">
        
        {/* Modal Header */}
        <div className="bg-gray-50 dark:bg-gray-800 px-6 py-4 flex justify-between items-center border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
              {selectedUser.email ? selectedUser.email[0].toUpperCase() : 'U'}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{selectedUser.name || 'User'}</h3>
              <p className="text-xs text-gray-500">{selectedUser.email || 'No Email'} • ID: {selectedUser.id}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-400 hover:text-gray-600 dark:hover:text-white" title="Flag User">
              <Flag size={18} />
            </button>
            <button className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-400 hover:text-red-500" title="Suspend">
              <UserX size={18} />
            </button>
            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white p-1">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="flex border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 overflow-x-auto shrink-0">
          <button onClick={() => setModalTab('overview')} className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${modalTab === 'overview' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}>
            Performance
          </button>
          <button onClick={() => setModalTab('risk')} className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${modalTab === 'risk' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}>
            Risk & Psychology
          </button>
          <button onClick={() => setModalTab('scoring')} className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${modalTab === 'scoring' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}>
            Scoring Engine
          </button>
          <button onClick={() => setModalTab('security')} className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${modalTab === 'security' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}>
            Security Flags
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6 overflow-y-auto bg-white dark:bg-gray-900">
          {/* OVERVIEW TAB */}
{modalTab === 'overview' && (
  <div className="space-y-6">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatBox label="EDGE Score" value={selectedUser.edge_score || 0} color="text-blue-500" />
      <StatBox label="Growth" value={`${selectedUser.growth || 0}%`} color={(selectedUser.growth || 0) >= 0 ? 'text-green-500' : 'text-red-500'} />
      <StatBox label="Net Profit" value={`$${selectedUser.net_profit || 0}`} color="text-gray-900 dark:text-white" />
      <StatBox label="Win Rate" value={`${selectedUser.win_rate || 0}%`} color="text-purple-500" />
    </div>

    {/* Equity Curve Section Removed */}

    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700/50">
      <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-4">Trading Statistics</h4>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-8 text-sm">
        <DetailRow label="Account Size" value={`$${selectedUser.account_size || 0}`} />
        <DetailRow label="Current Balance" value={`$${selectedUser.current_balance || 0}`} />
        <DetailRow label="Total Trades" value={selectedUser.total_trades || 0} />
        <DetailRow label="Active Days" value={selectedUser.active_days || 0} />
        <DetailRow label="Profit Factor" value={selectedUser.profit_factor || 'N/A'} />
        <DetailRow label="Max Drawdown" value={`${selectedUser.max_drawdown || 0}%`} isBad={!!(selectedUser.max_drawdown > 20)} />
      </div>
    </div>
  </div>
)}

          {/* RISK TAB */}
          {modalTab === 'risk' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-5 border border-gray-200 dark:border-gray-700/50">
                  <h4 className="text-sm font-bold text-red-500 mb-4 flex items-center gap-2">
                    <Target size={16} /> Risk Management
                  </h4>
                  <div className="space-y-4">
                    <DetailRow label="Risk Score" value={selectedUser.risk_score || 0} />
                    <DetailRow label="Consistency Score" value={selectedUser.consistency_score || 0} />
                    <DetailRow label="Risk Violations" value={selectedUser.risk_violations || 0} isBad={!!(selectedUser.risk_violations > 0)} />
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-5 border border-gray-200 dark:border-gray-700/50">
                  <h4 className="text-sm font-bold text-purple-500 mb-4 flex items-center gap-2">
                    <Brain size={16} /> Psychology
                  </h4>
                  <div className="space-y-4">
                    <DetailRow label="Discipline Score" value={selectedUser.discipline_score || 0} />
                    <DetailRow label="Tilt Events" value={selectedUser.tilt_events || 0} isBad={!!(selectedUser.tilt_events > 0)} />
                    <DetailRow label="Revenge Trades" value={selectedUser.revenge_trades || 0} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SCORING TAB */}
          {modalTab === 'scoring' && (
            <div className="space-y-6">
              <div className="space-y-4">
                <WeightSlider label="Discipline Weight" value={scoringWeights.discipline} onChange={(v) => setScoringWeights({...scoringWeights, discipline: v})} />
                <WeightSlider label="Risk Weight" value={scoringWeights.risk_management} onChange={(v) => setScoringWeights({...scoringWeights, risk_management: v})} />
                <WeightSlider label="Growth Weight" value={scoringWeights.growth} onChange={(v) => setScoringWeights({...scoringWeights, growth: v})} />
              </div>
              <div className="flex justify-end">
                <button onClick={handleRecalculateScores} disabled={isSaving} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 flex items-center gap-2">
                  {isSaving ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Recalculate
                </button>
              </div>
            </div>
          )}

          {/* SECURITY TAB */}
          {modalTab === 'security' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <ShieldAlert className="text-red-500" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Suspicious Growth Alert</p>
                    <p className="text-xs text-gray-400">Unusual profit velocity detected.</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-bold ${selectedUser.suspicious_growth ? 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400' : 'bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400'}`}>
                  {selectedUser.suspicious_growth ? 'FLAGGED' : 'CLEAN'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
)}
      {/* ACTION MODAL (Suspend / Unsuspend / Delete) */}
      {actionModal.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900/80 dark:bg-gray-950/90" onClick={() => setActionModal({ isOpen: false, user: null, type: null })}></div>
            
            <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-900 shadow-2xl rounded-2xl border border-gray-200 dark:border-gray-800">
              <div className="flex items-start gap-4">
                <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${
                  actionModal.type === 'delete' ? 'bg-red-100 dark:bg-red-500/10' : 
                  actionModal.type === 'suspend' ? 'bg-orange-100 dark:bg-orange-500/10' : 
                  'bg-green-100 dark:bg-green-500/10'
                } sm:mx-0 sm:h-10 sm:w-10`}>
                  {actionModal.type === 'delete' && <Trash2 className="text-red-600 dark:text-red-400" size={20} />}
                  {actionModal.type === 'suspend' && <UserX className="text-orange-600 dark:text-orange-400" size={20} />}
                  {actionModal.type === 'recover' && <CheckCircle className="text-green-600 dark:text-green-400" size={20} />}
                </div>
                <div className="mt-2 text-left flex-1">
                  <h3 className="text-lg font-bold leading-6 text-gray-900 dark:text-white">
                    {actionModal.type === 'delete' && 'Permanently Delete User'}
                    {actionModal.type === 'suspend' && 'Suspend User Account'}
                    {actionModal.type === 'recover' && 'Recover User Account'}
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {actionModal.type === 'delete' && `This will permanently remove ${actionModal.user?.email} and all their data. This action cannot be undone.`}
                    {actionModal.type === 'suspend' && `This will lock ${actionModal.user?.email}'s account. They will be logged out immediately.`}
                    {actionModal.type === 'recover' && `This will restore access for ${actionModal.user?.email}. They will be able to login again.`}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button type="button" className="inline-flex justify-center flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors" onClick={() => setActionModal({ isOpen: false, user: null, type: null })}>Cancel</button>
                <button type="button" className={`inline-flex justify-center flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 ${
                    actionModal.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : 
                    actionModal.type === 'suspend' ? 'bg-orange-500 hover:bg-orange-600' : 
                    'bg-green-600 hover:bg-green-700'
                  }`} onClick={handleConfirmAction} disabled={isSaving}>
                  {isSaving ? <Loader2 size={16} className="animate-spin mx-auto" /> : (actionModal.type === 'delete' ? 'Delete Permanently' : actionModal.type === 'suspend' ? 'Confirm Suspend' : 'Confirm Recover')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- HELPER COMPONENTS ---

function StatCard({ icon: Icon, label, value, color }) {
  const colors = { blue: 'text-blue-500 bg-blue-100 dark:bg-blue-500/10', yellow: 'text-yellow-500 bg-yellow-100 dark:bg-yellow-500/10', green: 'text-green-500 bg-green-100 dark:bg-green-500/10', purple: 'text-purple-500 bg-purple-100 dark:bg-purple-500/10' };
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 flex items-center gap-4 shadow-sm">
      <div className={`p-3 rounded-lg ${colors[color]}`}><Icon size={20} /></div>
      <div><p className="text-gray-400 text-[10px] md:text-xs uppercase">{label}</p><p className="text-lg md:text-xl font-bold text-gray-900 dark:text-white">{value}</p></div>
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
      <p className="text-gray-400 text-xs mb-1">{label}</p>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function DetailRow({ label, value, isBad }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-gray-500">{label}</span>
      <span className={`font-medium ${isBad ? 'text-red-500' : 'text-gray-700 dark:text-gray-200'}`}>{value}</span>
    </div>
  );
}

function WeightSlider({ label, value, onChange }) {
  return (
    <div>
      <div className="flex justify-between mb-2">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
        <span className="text-sm font-bold text-blue-500">{value}%</span>
      </div>
      <input type="range" min="0" max="100" value={value} onChange={(e) => onChange(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500" />
    </div>
  );
}

function SettingToggle({ label, description, value, onChange, isDanger }) {
  return (
    <div className={`bg-white dark:bg-gray-900 border ${isDanger && value ? 'border-red-500/50' : 'border-gray-200 dark:border-gray-800'} rounded-xl p-5 flex justify-between items-center gap-4 shadow-sm`}>
      <div><h3 className={`font-medium ${isDanger && value ? 'text-red-500' : 'text-gray-900 dark:text-white'}`}>{label}</h3><p className="text-xs text-gray-400 mt-1">{description}</p></div>
      <button onClick={() => onChange(!value)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${value ? (isDanger ? 'bg-red-500' : 'bg-blue-500') : 'bg-gray-300 dark:bg-gray-700'}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${value ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );
}
